import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, ContactShadows } from '@react-three/drei';
import { Fragment, Suspense, useEffect, useRef } from 'react';
import { Box3 } from 'three';
import type * as THREE from 'three';
import { RampConfig } from '@gorampe/calc-engine';
import { useRampScene } from './hooks/useRampScene';
import {
  Tread,
  PlatformDeck,
  HandRailTube,
  HandrailEndCap,
  Patte,
  CornerPost,
  Transition,
  BuildingWall,
} from './parametric';
import { SCALE_IN_TO_UNIT } from './types';
import { Rail } from './hardware/Rail';
import { RivetField } from './hardware/RivetField';
import { BoltField } from './hardware/BoltField';
import { computeCameraFit } from './camera/autoFit';
import { groundTexture } from './materials/proceduralTextures';
import { SelectionOutline } from './selection/SelectionOutline';
import { CameraFollowSelection } from './selection/CameraFollowSelection';

interface Props {
  config: RampConfig;
  interactive?: boolean;
  /**
   * RampScene props:
   *  - lowQuality=true: DPR=1, frameloop='demand', no shadows, no ContactShadows, ambient only.
   *      Use for embedded thumbnails or mobile.
   *  - lowQuality=false (default): full quality, frameloop='always', shadows enabled, directional + hemisphere + contactShadows.
   */
  lowQuality?: boolean;
}

function SceneContents({ config, lowQuality }: { config: RampConfig; lowQuality: boolean }) {
  const layout = useRampScene(config);
  return (
    <>
      {layout.platforms.map((p) => (
        <PlatformDeck key={p.id} placement={p} floor={config.floor} />
      ))}
      {layout.treads.map((t) => (
        <Tread key={t.id} placement={t} floor={config.floor} />
      ))}
      {layout.pattes.map((p) => (
        <Patte key={p.id} placement={p} />
      ))}
      {layout.cornerPosts.map((cp) => (
        <CornerPost
          key={cp.id}
          position={[cp.position.x, cp.position.y, cp.position.z]}
          heightIn={cp.heightIn}
          refId={cp.id}
          {...(cp.partNumber !== undefined ? { partNumber: cp.partNumber } : {})}
        />
      ))}
      {layout.transitions.map((t) => (
        <Transition
          key={t.id}
          position={[t.position.x, t.position.y, t.position.z]}
          rotationY={t.rotationY}
          widthIn={t.widthIn}
          refId={t.id}
          {...(t.partNumber !== undefined ? { partNumber: t.partNumber } : {})}
        />
      ))}
      {layout.landings.map((l) => (
        <PlatformDeck
          key={l.id}
          placement={{
            id: l.id,
            kind: 'rest-landing',
            position: l.position,
            widthIn: l.widthIn,
            depthIn: l.depthIn,
          }}
          floor={config.floor}
        />
      ))}
      {layout.rails.map((r) => (
        <Rail key={r.id} placement={r} />
      ))}
      {!lowQuality &&
        config.railType !== 'aucun' &&
        layout.rails.map((r) => {
          // Handrail Y: rail top (heightIn) + 6" HR_OFFSET_ABOVE_RAIL_IN (added inside HandRailTube)
          const hrY = r.position.y + r.heightIn * SCALE_IN_TO_UNIT + 6 * SCALE_IN_TO_UNIT;
          const halfLen = (r.lengthIn / 2) * SCALE_IN_TO_UNIT;
          // Compute tube end positions in world space based on rotationY
          // tube extrudes along local Z; rotationY rotates local Z into world axis
          let end1: [number, number, number];
          let end2: [number, number, number];
          const absRot = Math.abs(r.rotationY);
          if (absRot < 0.01 || Math.abs(absRot - Math.PI) < 0.01) {
            // rotationY ≈ 0 or π → local Z = ±world Z
            end1 = [r.position.x, hrY, r.position.z - halfLen];
            end2 = [r.position.x, hrY, r.position.z + halfLen];
          } else {
            // rotationY ≈ ±π/2 → local Z = ±world X
            const sign = r.rotationY > 0 ? 1 : -1;
            end1 = [r.position.x - sign * halfLen, hrY, r.position.z];
            end2 = [r.position.x + sign * halfLen, hrY, r.position.z];
          }
          // FIX: pitchAngle must follow the rail's incline (was hardcoded 0).
          // For pitched rails the handrail must descend with the ramp, not float horizontally.
          const pitchAngle = r.pitchAngle ?? 0;
          // Compute end caps with pitch correction: ends rise/fall by halfLen × sin(pitch)
          if (Math.abs(pitchAngle) > 0.001) {
            const dy = halfLen * Math.sin(pitchAngle);
            // For rotY=0 (arm 1, +Z descent), end2 (z+halfLen) is downhill → lower Y
            // For rotY=π (arm 2, -Z descent), end1 (z-halfLen) is downhill → lower Y
            if (absRot < 0.01) {
              end1[1] = hrY + dy;
              end2[1] = hrY - dy;
            } else if (Math.abs(absRot - Math.PI) < 0.01) {
              end1[1] = hrY - dy;
              end2[1] = hrY + dy;
            } else {
              // rotY=±π/2 (L90 arm 2) — pitch direction is along X
              end1[1] = hrY + dy;
              end2[1] = hrY - dy;
            }
          }
          return (
            <Fragment key={`hr-${r.id}`}>
              <HandRailTube
                lengthIn={r.lengthIn}
                position={[r.position.x, r.position.y + r.heightIn / 12, r.position.z]}
                rotationY={r.rotationY}
                pitchAngle={pitchAngle}
                refId={`hr-${r.id}`}
              />
              <HandrailEndCap position={end1} refId={`hr-${r.id}-cap1`} />
              <HandrailEndCap position={end2} refId={`hr-${r.id}-cap2`} />
            </Fragment>
          );
        })}
      {layout.hardwareGroups.map((g, i) => {
        if (g.geometry.startsWith('rivet-')) {
          return <RivetField key={`hw-${i}`} group={g} />;
        }
        if (g.geometry.startsWith('bolt-')) {
          return <BoltField key={`hw-${i}`} group={g} />;
        }
        return null;
      })}
      {/* Ground — larger and more visible so users see the floor clearly */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]} receiveShadow>
        <planeGeometry args={[60, 60]} />
        <meshStandardMaterial map={groundTexture()} roughness={0.95} metalness={0} />
      </mesh>
      {/* Building wall behind head platform — provides visual context: building entry at top, ground at bottom */}
      {(() => {
        const headPlatform = layout.platforms.find((p) => p.kind === 'head');
        if (!headPlatform) return null;
        return (
          <BuildingWall
            platformZ={headPlatform.position.z}
            widthIn={config.widthIn}
            platformDepthIn={headPlatform.depthIn}
          />
        );
      })()}
      {/* Contact shadows — disabled in lowQuality mode */}
      {!lowQuality && (
        <ContactShadows
          position={[0, -0.04, 0]}
          opacity={0.45}
          scale={20}
          blur={2.5}
          far={10}
          resolution={256}
        />
      )}
      <AutoFitter />
      <CameraFollowSelection />
    </>
  );
}

interface OrbitLike extends THREE.EventDispatcher {
  target: THREE.Vector3;
  update(): void;
}

function AutoFitter() {
  const { scene, camera, controls } = useThree();
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current) return;
    const bbox = new Box3().setFromObject(scene);
    if (bbox.isEmpty()) return;
    const fit = computeCameraFit(bbox);
    camera.position.copy(fit.position);
    camera.lookAt(fit.target);
    if (controls && 'target' in controls) {
      const orbit = controls as OrbitLike;
      orbit.target.copy(fit.target);
      orbit.update();
    }
    didFit.current = true;
  });
  return null;
}

/**
 * RampScene props:
 *  - lowQuality=true: DPR=1, frameloop='demand', no shadows, no ContactShadows, ambient only.
 *      Use for embedded thumbnails or mobile.
 *  - lowQuality=false (default): full quality, frameloop='always', shadows enabled, directional + hemisphere + contactShadows.
 */
export function RampScene({ config, interactive = true, lowQuality = false }: Props) {
  return (
    <Canvas
      camera={{ position: [8, 6, 12], fov: 35 }}
      style={{ width: '100%', height: '100%' }}
      dpr={lowQuality ? 1 : [1, 1.5]}
      frameloop={lowQuality ? 'demand' : 'always'}
      shadows={!lowQuality}
      gl={{ antialias: !lowQuality, alpha: false }}
    >
      <color attach="background" args={['#0F172A']} />
      {/* Ambient — always present */}
      <ambientLight intensity={lowQuality ? 0.6 : 0.35} />
      {/* Hemisphere sky/ground fill — full quality only */}
      {!lowQuality && <hemisphereLight args={['#dbeafe', '#1e3a5f', 0.4]} />}
      {/* Key directional light with shadows */}
      {!lowQuality && (
        <directionalLight
          position={[8, 12, 6]}
          intensity={1.2}
          castShadow
          shadow-mapSize={[1024, 1024]}
          shadow-camera-near={0.5}
          shadow-camera-far={50}
          shadow-camera-left={-12}
          shadow-camera-right={12}
          shadow-camera-top={12}
          shadow-camera-bottom={-12}
          shadow-bias={-0.0005}
        />
      )}
      {/* Fill light from opposite side */}
      {!lowQuality && <directionalLight position={[-5, 4, -4]} intensity={0.35} />}
      {interactive && <OrbitControls makeDefault enableDamping={!lowQuality} autoRotate={false} />}
      <Suspense fallback={null}>
        <SelectionOutline enabled={!lowQuality}>
          <SceneContents config={config} lowQuality={lowQuality} />
        </SelectionOutline>
      </Suspense>
    </Canvas>
  );
}
